<template>
  <div class="switch-field" :class="{ disabled: disabled }">
    <div class="switch-row">
      <div class="switch-label">{{ label }}</div>
      <EMToggleSwitch
        :model-value="modelValue"
        @update:model-value="handleSwitchChange"
        :disabled="disabled"
      />
    </div>

    <div v-if="modelValue && hasTextInput" class="text-input-container">
      <EMInput
        :model-value="textValue"
        :label="inputLabel"
        :placeholder="inputPlaceholder"
        :disabled="disabled"
        :error-text="error"
        :show-error="!!error"
        @update:model-value="handleTextChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import EMToggleSwitch from '@/common/components/ui/switch/EMToggleSwitch.vue';
import EMInput from '@/common/components/ui/input/EMInput.vue';

interface Props {
  modelValue: boolean;
  label: string;
  disabled?: boolean;
  textValue?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  error?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  textValue: '',
  inputLabel: '',
  inputPlaceholder: '',
  error: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'update:textValue': [value: string];
  change: [value: boolean];
  'text-change': [value: string];
}>();

const hasTextInput = computed(() => props.inputLabel || props.inputPlaceholder);

const handleSwitchChange = (value: boolean) => {
  emit('update:modelValue', value);
  emit('change', value);

  if (!value && hasTextInput.value) {
    emit('update:textValue', '');
    emit('text-change', '');
  }
};

const handleTextChange = (value: string | number) => {
  const strValue = String(value);
  emit('update:textValue', strValue);
  emit('text-change', strValue);
};
</script>

<style scoped>
.switch-field {
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border: 1px solid #e5e5e5;
  border-radius: var(--ion-radius-lg);
  background-color: var(--ion-input-background);
  gap: 12px;
}

.switch-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.switch-field.disabled {
  color: var(--ion-text-color-disabled);
  background-color: var(--ion-input-background-color-disabled);
  border-color: var(--ion-input-border-color-disabled);
  cursor: not-allowed;
}

.switch-label {
  font-size: 14px;
  color: var(--ion-text-color);
  font-weight: 400;
  flex: 1;
  margin-right: 12px;
}

.switch-field.disabled .switch-label {
  color: var(--ion-text-color-disabled);
}

.text-input-container {
  display: flex;
  flex-direction: column;
}
</style>
